/**
 * 올인원 — QR스티커 탭
 * docs/tools_all.md §8
 *
 * - 8-1 가족 체크박스 리스트 (기본: 가족 전원 체크)
 * - 8-2 프린터 Select (USB 우선) + N장 출력
 * - 8-3 프린터 상태 30초 폴링 (USB는 브릿지 직접)
 * - 8-4 용지 부족 시 출력 비활성
 * - 8-5 USB 실패는 printQrBatch 내부에서 reportUsbPrintError 자동 호출
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Card, Checkbox, Tag, Space, Button, Select, Empty, Alert, Typography,
  message as messageApi,
} from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getStorePrinters, checkUsbBridge, getPrinterStatus,
  getPrinterPaperStatus, printQrBatch,
} from '../../services/api';

const { Text } = Typography;

const POLL_MS = 30_000;

const getDisplayName = (m) => m?.nickname || m?.name || (m?.member_type === 'CHILD' ? '자녀' : '회원');

export default function StickerTab({
  scannedMember, family, beginAction, endAction, onLog,
}) {
  const targets = family.length ? family : (scannedMember ? [scannedMember] : []);

  const [printers, setPrinters] = useState([]);
  const [printerId, setPrinterId] = useState(null);
  const [status, setStatus] = useState(null); // { status, reachable, connection_type, ... }
  const [paper, setPaper] = useState(null); // { remaining }
  const [selected, setSelected] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);
  const pollRef = useRef(null);

  // 식별 회원 / 가족 로드 시: 가족 전원 기본 체크 (없으면 본인만)
  useEffect(() => {
    if (!scannedMember) { setSelected(new Set()); return; }
    const ids = (family && family.length)
      ? family.map((m) => m.member_id)
      : [scannedMember.member_id];
    setSelected(new Set(ids));
  }, [scannedMember, family]);

  // 프린터 목록 (USB 우선 정렬 + USB는 브릿지 reachable 보정)
  const loadPrinters = useCallback(async () => {
    try {
      const res = await getStorePrinters();
      const list = res.printers || [];
      const hasUsb = list.some((p) => p.connection_type === 'usb');
      let filtered = list;
      if (hasUsb) {
        const bridge = await checkUsbBridge();
        filtered = list.filter((p) => p.connection_type !== 'usb' || bridge.connected);
      }
      // USB 우선 정렬
      filtered.sort((a, b) => (a.connection_type === 'usb' ? -1 : 1) - (b.connection_type === 'usb' ? -1 : 1));
      setPrinters(filtered);
      if (filtered.length && !filtered.some((p) => p.id === printerId)) {
        const reachable = filtered.find((p) => p.reachable) || filtered[0];
        setPrinterId(reachable.id);
      }
    } catch {
      setPrinters([]);
    }
  }, [printerId]);

  useEffect(() => { loadPrinters(); }, []);

  // 상태 + 용지 폴링
  const pollStatus = useCallback(async () => {
    if (!printerId) return;
    try {
      const res = await getPrinterStatus(printerId);
      const printer = res.printer;
      if (printer?.connection_type === 'usb') {
        const bridge = await checkUsbBridge();
        setStatus({
          ...printer,
          reachable: bridge.connected,
          status: bridge.connected ? 'ready' : 'unreachable',
        });
      } else {
        setStatus(printer);
      }
    } catch { setStatus(null); }
    try {
      const ps = await getPrinterPaperStatus();
      const me = (ps.printers || []).find((p) => p.printer_id === printerId);
      setPaper(me || null);
    } catch { setPaper(null); }
  }, [printerId]);

  useEffect(() => {
    pollStatus();
    pollRef.current = setInterval(pollStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [pollStatus]);

  const allChecked = targets.length > 0 && targets.every((m) => selected.has(m.member_id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(targets.map((m) => m.member_id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const remaining = paper?.remaining ?? null;
  const paperShort = remaining != null && remaining < selectedIds.length;
  const printerReady = status?.status === 'ready' || status?.reachable === true;

  const printDisabled = printing || !printerId || !printerReady || paperShort || selectedIds.length === 0;

  const handlePrint = async () => {
    if (printDisabled) return;
    const actionId = `print-${Date.now()}`;
    setPrinting(true);
    beginAction(actionId);
    try {
      const res = await printQrBatch(selectedIds, printerId);
      if (res?.no_printer) {
        messageApi.error(res.message || 'USB 프린터 미연결');
        onLog?.(`❌ 출력 실패: USB 프린터 미연결`);
      } else if (res?.success === false) {
        messageApi.error(res.message || '출력 실패');
        onLog?.(`❌ 출력 실패: ${res.message || ''}`);
      } else {
        messageApi.success(`${selectedIds.length}장 출력 요청 완료`);
        onLog?.(`✅ QR 스티커 ${selectedIds.length}장 출력`);
        await pollStatus();
      }
    } catch (err) {
      messageApi.error(err?.message || '출력 실패');
      onLog?.(`❌ 출력 실패: ${err?.message || ''}`);
    } finally {
      endAction(actionId);
      setPrinting(false);
    }
  };

  if (!scannedMember) return <Empty description="회원을 검색 해 주세요" />;

  const statusLabel = (() => {
    if (!status) return { text: '⚪ 상태 알 수 없음', color: '#90A4AE' };
    if (status.status === 'ready' || status.reachable) return { text: '🟢 준비됨', color: '#43A047' };
    if (status.status === 'unreachable') return { text: '🔴 USB 프린터 미연결', color: '#C62828' };
    return { text: `⚪ ${status.status || '비활성'}`, color: '#90A4AE' };
  })();

  return (
    <div className="sticker-tab">
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          style={{ minWidth: 220 }}
          value={printerId}
          onChange={setPrinterId}
          placeholder="프린터 선택"
          options={printers.map((p) => ({
            value: p.id,
            label: `${p.connection_type === 'usb' ? '🔌 USB · ' : '🌐 NET · '}${p.name || p.id}`,
          }))}
        />
        <Button size="small" icon={<ReloadOutlined />} onClick={() => { loadPrinters(); pollStatus(); }} className="sticker-refresh-btn">새로고침</Button>
        <span style={{ color: statusLabel.color, fontWeight: 600 }}>{statusLabel.text}</span>
        {remaining != null && (
          <Text type={paperShort ? 'danger' : 'secondary'}>
            잔여 용지 {remaining}장
          </Text>
        )}
      </Space>

      <Space style={{ marginBottom: 8 }}>
        <Checkbox checked={allChecked} indeterminate={!allChecked && selected.size > 0} onChange={toggleAll}>
          전체 {allChecked ? '해제' : '선택'}
        </Checkbox>
        <Text type="secondary">선택 {selectedIds.length} / {targets.length}</Text>
      </Space>

      <div className="sticker-family-list">
        {targets.map((m) => (
          <Card
            key={m.member_id}
            size="small"
            className={`sticker-family-card ${selected.has(m.member_id) ? 'selected' : ''}`}
            onClick={() => toggleOne(m.member_id)}
            hoverable
          >
            <Space>
              <Checkbox checked={selected.has(m.member_id)} onChange={() => toggleOne(m.member_id)} />
              <span style={{ fontWeight: 600 }}>{getDisplayName(m)}</span>
              {m.member_type === 'CHILD'
                ? <Tag color="purple">자녀</Tag>
                : <Tag color="blue">보호자</Tag>}
            </Space>
          </Card>
        ))}
      </div>

      {paperShort && (
        <Alert
          type="warning" showIcon
          style={{ marginTop: 12 }}
          message={`용지 부족: 잔여 ${remaining}장 / 선택 ${selectedIds.length}장`}
          description="용지 교체는 [프린터 관리] 페이지에서 진행하세요."
        />
      )}

      <Button
        type="primary" size="large" block icon={<PrinterOutlined />}
        style={{ marginTop: 12 }}
        loading={printing}
        disabled={printDisabled}
        onClick={handlePrint}
      >
        🖨️ {selectedIds.length}장 출력
      </Button>
    </div>
  );
}
